import imageCompression from "browser-image-compression";
import i18n from "../i18n";
import { QualificationsType, TrainingTypes } from "../context";
import {
  QualificationListType,
  TrainingHeadersType,
  TrainingsListType,
} from "./TrainingsHeaderView";

// Semantic order for qualification levels — single source of truth used by
// both the QualificationsApp dialog (right-panel tabs) and MethodModulesList
// (left-panel level accordions). Levels not in this map sort to the end.
export const QUALIFICATION_LEVEL_ORDER: Record<string, number> = {
  Start: 0,
  Basic: 1,
  Intermediate: 2,
  Specialist: 3,
};

// utils/emailValidation.ts

const VALID_EXTERNAL_EMAIL_DOMAINS = process.env
  .REACT_APP_VALID_EXTERNAL_EMAIL_DOMAINS
  ? process.env.REACT_APP_VALID_EXTERNAL_EMAIL_DOMAINS.split(",").map(
      (domain) => domain.trim().toLowerCase(),
    )
  : [];

const VALID_EMAIL_DOMAINS = [...VALID_EXTERNAL_EMAIL_DOMAINS, "kone.com"];

export const isValidEmailDomain = (email: string): boolean => {
  if (!email) return false;

  const trimmedEmail = email.trim();

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  if (!emailRegex.test(trimmedEmail)) return false;

  const domain = trimmedEmail.split("@")[1].toLowerCase();

  return VALID_EMAIL_DOMAINS.includes(domain);
};

export type TrainingTypesResponseType = {
  trainingTypeId: number;
  trainingType: string;
}[];

export type UserQualificationType = {
  qualificationTypeId: number;
  trainingId: number;
  trainingName: string;
  trainingTypeId: number;
  passportLevel: number;
  expirationDate: string;
  completionDate: string;
  state: string;
  qualificationLevel?: string;
  qualificationLevelId?: number | null;
}[];

export type UserQualificationResponseType = {
  userId: string;
  qualifications: UserQualificationType;
};

export const validateField = (
  field: string,
  value: unknown,
  emailOptional = false,
) => {
  let error = "";
  const normalizedStringValue =
    typeof value === "string" ? value.trim() : String(value ?? "").trim();
  const isEmptyValue =
    value == null ||
    (typeof value === "string" && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0);
  switch (field) {
    case "firstName":
    case "lastName":
      if (isEmptyValue) {
        error = i18n.t("validation.fieldRequired");
      } else if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(normalizedStringValue)) {
        console.log("value = ", value);
        error = i18n.t("validation.onlyLettersAllowed");
      }
      break;
    case "frontline":
    case "country":
    case "company":
    case "businessTypeId":
    case "businessTypeIds":
      if (isEmptyValue) {
        error = i18n.t("validation.fieldRequired");
      }
      break;
    // `emailId` is the canonical field name, matching the base field catalog's
    // formKey and payloadKeyMapper. Both admin forms use it.
    //
    // No case for a country's own fields here: whether one is required comes
    // from its definition (is_mandatory), and the form reads that from the
    // field schema rather than from a branch in this switch.
    case "emailId":
      // Email is optional for subcontractor employees (they may have no email and
      // log in with a generated username/password). Still validate the domain when a
      // value is provided. Mandatory for KONE internal (emailOptional = false).
      if (normalizedStringValue.length === 0)
        return emailOptional ? "" : i18n.t("validation.emailRequired");
      if (!isValidEmailDomain(normalizedStringValue)) return i18n.t("validation.invalidEmail");
      return ""; // ✅ MUST CLEAR ERROR
    default:
      break;
  }
  return error;
};

export const sortHeaders = (
  headers: TrainingHeadersType[],
  trainingTypes: TrainingTypes,
) => {
  // Ensure the headers are sorted as Global Safety, Global Methods, and Local Requirements
  headers.sort((a, b) => {
    // Define the order of training types as per the requirement and naming should be consistent with database
    const order = [
      trainingTypes.GLOBAL_SAFETY,
      trainingTypes.METHOD,
      trainingTypes.LOCAL_SAFETY,
    ];
    console.log("order", order);
    console.log("a and b", a, b);
    // Sort based on the defined order
    return order.indexOf(a.id) - order.indexOf(b.id);
  });
  console.log("headers after sorting", headers);
  return headers;
};

export const intialTrainingsHeaderMapper = (
  data: QualificationListType,
  trainingTypes: TrainingTypes,
): TrainingHeadersType[] => {
  const isTrainingTypeHeaders = data.titleKey === "trainingTypeId";
  const isTrainingHeaders = data.titleKey === "trainingId";
  const headers = data.qualificationHeaders.map((header) => {
    return {
      name: header.title,
      id: header.titleId,
      trainingTypeId: isTrainingTypeHeaders ? header.titleId : 100, // Default to GLOBAL if not specified
      isTraining: isTrainingHeaders,
    } as unknown as TrainingHeadersType;
  });
  // Sort the headers to ensure consistent order
  return sortHeaders(headers, trainingTypes);
};

export const trainingsHeaderMapper = (
  data: TrainingsListType,
): TrainingHeadersType[] => {
  const headers = data.map((header) => {
    return {
      name: header.trainingName,
      id: header.trainingId,
      passportLevel: header.passportTypeId,
      trainingTypeId: header.trainingTypeId || 100,
      isTraining: true,
    } as unknown as TrainingHeadersType;
  });
  return headers;
};

export const calulateExpiryDate = (
  trainingType: number,
  trainingTypes: TrainingTypes,
  validForYears?: number | null,
  completionDate?: string,
): string => {
  // Use completionDate as base if provided, otherwise use today
  const baseDate = completionDate ? new Date(completionDate) : new Date();
  if (isNaN(baseDate.getTime())) return "";

  // Use validForYears from database if available to know expiration years
  if (validForYears) {
    const expiryDate = new Date(baseDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + validForYears);
    return expiryDate.toISOString().split("T")[0];
  }

  // If valid_years is null then fallback logic for GLOBAL_SAFETY and LOCAL_REQUIREMENTS
  // GLOBAL_SAFETY: 5 years default expiration
  if (trainingType === trainingTypes.GLOBAL_SAFETY) {
    const expiryDate = new Date(baseDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 5);
    return expiryDate.toISOString().split("T")[0];
  }
  // LOCAL_SAFETY: 2 years default expiration
  else if (trainingType === trainingTypes.LOCAL_SAFETY) {
    const expiryDate = new Date(baseDate);
    expiryDate.setFullYear(expiryDate.getFullYear() + 2);
    return expiryDate.toISOString().split("T")[0];
  }

  return "";
};

export const formatTrainingTypesResponse = (
  trainingTypeResponse: TrainingTypesResponseType,
) => {
  const trainingTypes: TrainingTypes = {
    GLOBAL_SAFETY:
      trainingTypeResponse.find((trainingType) =>
        trainingType.trainingType.toLowerCase().includes("global"),
      )?.trainingTypeId || 0,
    LOCAL_SAFETY:
      trainingTypeResponse.find((trainingType) =>
        trainingType.trainingType.toLowerCase().includes("local"),
      )?.trainingTypeId || 0,
    METHOD:
      trainingTypeResponse.find((trainingType) =>
        trainingType.trainingType.toLowerCase().includes("method"),
      )?.trainingTypeId || 0,
  };
  return trainingTypes;
};

export const formatUserQualificationResponse = (
  userQualifications: UserQualificationType,
) => {
  const formattedResponse: QualificationsType[] = userQualifications.map(
    (item) => ({
      title: item.trainingName,
      completionDate: item.completionDate,
      expirationDate: item.expirationDate,
      type: item.trainingTypeId,
      trainingId: item.trainingId,
      qualificationTypeId: item.qualificationTypeId,
      state: item.state,
      qualificationLevel: item.qualificationLevel,
      qualificationLevelId: item.qualificationLevelId,
    }),
  );
  return formattedResponse;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 *
 * @param file - The file to be compressed
 * @description Compresses an image file to reduce its size while maintaining quality.
 * The function uses the `browser-image-compression` library to perform the compression.
 * @returns compressed file as a Promise<File>
 */
export const compressImage = async (file: File): Promise<File> => {
  const compressionOptions = {
    maxSizeMB: 2, // Maximum size in MB
    maxWidthOrHeight: 1920, // Maximum width or height
    useWebWorker: true, // Use web workers for better performance
  };

  return await imageCompression(file, compressionOptions);
};
